import {
  Children,
  forwardRef,
  isValidElement,
  useMemo,
  type ReactNode,
} from 'react';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { devLog } from '../../core/utils/logging.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { Content as NotificationSettingsContent } from '../../notifications/notification-settings/components/Content.js';
import { NotificationPermissionInfo } from '../../notifications/notification-settings/components/PermissionInfo.js';
import { NotificationToggleButton } from '../../notifications/notification-settings/components/ToggleButton.js';
import { NotificationProvider } from '../../notifications/notification-settings/NotificationSettings.js';
import { useOverlayWithTrigger } from '../../overlays/hooks/useOverlayWithTrigger.js';
import { Overlay } from '../../overlays/overlay/Overlay.js';
import { Content } from './components/Content.js';
import { NotifyButtonTrigger } from './components/Trigger.js';
import { NotifyButtonContext } from './contexts/NotifyButtonContext.js';
import type { NotifyButtonProps } from './notify-button-types.js';
import * as styles from './NotifyButton.sty.js';

const COMPONENT_NAME = 'NotifyButton';

/** Iterates over child nodes to extract the content component slot. */
function getContentSlot(children: ReactNode) {
  let content: ReactNode;

  Children.forEach(children, (child) => {
    if (isValidElement(child)) {
      if (isSlot(child, Content)) {
        content = child;
      } else if (child) {
        devLog('The used child is not supported by the NotifyButton.');
      }
    } else if (child) {
      devLog('The used child is not supported by the NotifyButton.');
    }
  });

  return content;
}

/**
 * NotifyButton component
 */
const RootNotifyButton = forwardRef<HTMLButtonElement, NotifyButtonProps>(
  (props, ref) => {
    const {
      children,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      className: consumerClassName,
      style: consumerStyle,
      disabled,
      size = 'default',
      variant = 'default',
      type,
      config,
      readOnly,
      showLabel = false,
      ...remainingProps
    }: NotifyButtonProps = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    const overlayAriaLabelledBy = useId();

    const {
      targetRef,
      triggerRef,
      overlayRef,
      overlayProps,
      overlayState,
      overlayTriggerProps,
    } = useOverlayWithTrigger({
      placement: 'bottom-start',
      placementFallbacks: [
        'bottom-start',
        'bottom-end',
        'top-start',
        'top-end',
      ],
      offset: 4,
      disabled: disabled || readOnly,
      'aria-labelledby': overlayAriaLabelledBy,
    });
    const rootRef = useMergeRefs([ref, triggerRef, targetRef]);
    const contentSlot = useMemo(() => getContentSlot(children), [children]);

    const notifyButtonContextValue = useMemo(
      () => ({
        closeOverlay: overlayState.close,
        overlayAriaLabelledBy,
      }),
      [overlayAriaLabelledBy, overlayState.close],
    );

    return (
      <NotifyButtonContext.Provider value={notifyButtonContextValue}>
        <NotificationProvider type={type} config={config}>
          <NotifyButtonTrigger
            ref={rootRef}
            data-testid={dataTestId}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
            className={consumerClassName}
            style={consumerStyle}
            disabled={disabled}
            size={size}
            variant={variant}
            showLabel={showLabel}
            triggerProps={overlayTriggerProps}
            aria-disabled={readOnly}
            {...mergeProps(restProps, behavioralTrackingProps)}
            data-dt-component={COMPONENT_NAME}
          />
          <Overlay
            ref={overlayRef}
            isOpen={overlayState.isOpen}
            className={styles.overlay}
            overlayProps={overlayProps}
          >
            <Flex flexDirection="column" gap={12}>
              {contentSlot ?? <NotificationSettingsContent />}
              <Flex alignItems="center">
                <NotificationToggleButton />
                <NotificationPermissionInfo />
              </Flex>
            </Flex>
          </Overlay>
        </NotificationProvider>
      </NotifyButtonContext.Provider>
    );
  },
);

(
  RootNotifyButton as typeof RootNotifyButton & { displayName: string }
).displayName = COMPONENT_NAME;

/**
 * The `NotifyButton` lets users turn notifications about resource updates on or off. Common use cases include notifications about updates to saved filters, workflows, or entities. Users will be notified via e-mail.
 * @public
 */
export const NotifyButton = Object.assign(RootNotifyButton, {
  Content,
});
